import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './landing.css';
import photo from 'assets/era.png';

const stack = [
  ['Frontend', ['React', 'Next.js', 'Vue.js', 'TypeScript', 'JavaScript']],
  ['Backend', ['Node.js', 'NestJS', 'Express', 'PHP', 'Laravel']],
  ['Database', ['PostgreSQL', 'SQL']],
  [
    'Architecture',
    ['REST APIs', 'Authentication', 'Integrations', 'Automation']
  ],
  ['Infrastructure', ['Git', 'GitHub', 'Vercel', 'Cloud / Deployment']]
];

const thinking = [
  [
    '01 — Systems',
    'Designing applications around real business workflows rather than isolated features.'
  ],
  [
    '02 — APIs',
    'Connecting internal systems with third-party platforms through reliable API integrations.'
  ],
  [
    '03 — Automation',
    'Replacing repetitive operational work with software-driven workflows.'
  ],
  [
    '04 — Performance',
    'Finding bottlenecks across frontend, backend, database and infrastructure.'
  ],
  [
    '05 — Search',
    'Understanding how application architecture affects crawling, rendering and indexing.'
  ]
];

const advantages = [
  'Technical SEO',
  'GEO / AI Search',
  'Website performance',
  'Structured data',
  'Crawling, rendering, indexing',
  'Analytics implementation',
  'Conversion tracking',
  'Digital growth'
];

const liveWorks = [
  {
    name: 'Simbelmawa',
    org: 'Ministry of Higher Education, Science and Technology',
    url: 'https://simbelmawa.kemdiktisaintek.go.id/v2/',
    host: 'simbelmawa.kemdiktisaintek.go.id'
  },
  {
    name: 'LDI BPTI',
    org: 'Ministry of Primary and Secondary Education, debate competition platform',
    url: 'https://lombadebat-bpti.kemendikdasmen.go.id/auth/login',
    host: 'lombadebat-bpti.kemendikdasmen.go.id'
  },
  {
    name: 'SleekEV',
    org: 'Public website',
    url: 'https://www.sleekev.com/',
    host: 'sleekev.com'
  }
];

const Case = ({ featured, tag, title, sub, rows, chips }) => (
  <article className={`era-case${featured ? ' featured' : ''}`}>
    <header className="era-case-head">
      <h3>{title}</h3>
      <span className="era-tag">{tag}</span>
    </header>
    <p className="era-case-sub">{sub}</p>
    <dl className="era-case-grid">
      {rows.map(([term, body]) => (
        <React.Fragment key={term}>
          <dt>{term}</dt>
          <dd>
            {Array.isArray(body) ? (
              <ul>
                {body.map(b => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            ) : (
              body
            )}
          </dd>
        </React.Fragment>
      ))}
    </dl>
    <div className="era-chips">
      {chips.map(c => (
        <span key={c}>{c}</span>
      ))}
    </div>
  </article>
);

Case.propTypes = {
  featured: PropTypes.bool,
  tag: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  sub: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  chips: PropTypes.arrayOf(PropTypes.string).isRequired
};

const links = [
  ['Engineering', '#engineering'],
  ['Work', '#work'],
  ['Live', '#live'],
  ['Search & growth', '#search'],
  ['Contact', '#contact']
];

const Landing = () => {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);
  return (
    <div className="era">
      <nav className="era-nav">
        <div className="era-wrap">
          <a className="era-brand" href="#top" onClick={close}>
            eradyan
          </a>
          <ul className="era-nav-links">
            {links.map(([label, href]) => (
              <li key={href}>
                <a href={href}>{label}</a>
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="era-burger"
            aria-label="Open menu"
            aria-expanded={open}
            onClick={() => setOpen(true)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
        <div
          className={`era-drawer-backdrop${open ? ' open' : ''}`}
          onClick={close}
        />
        <aside
          className={`era-drawer${open ? ' open' : ''}`}
          aria-hidden={!open}
        >
          <button
            type="button"
            className="era-drawer-close"
            aria-label="Close menu"
            onClick={close}
          >
            ×
          </button>
          <ul>
            {links.map(([label, href]) => (
              <li key={href}>
                <a href={href} onClick={close} tabIndex={open ? 0 : -1}>
                  {label}
                </a>
              </li>
            ))}
          </ul>
        </aside>
      </nav>

      <header className="era-hero" id="top">
        <div className="era-wrap">
          <div className="era-hero-grid">
            <div>
              <span className="era-label">Eradyan · Indonesia</span>
              <h1 style={{ marginTop: 18 }}>
                Software Engineer.
                <br />
                <span>I build the systems behind digital growth.</span>
              </h1>
              <p className="era-lede">
                I design architectures, build backends and frontends, ship REST
                APIs and integrations, and work directly in production
                codebases. That foundation is why I also understand technical
                SEO, GEO and the way software drives business growth.
              </p>
              <div className="era-cta">
                <a className="era-btn primary" href="#work">
                  See the engineering work
                </a>
                <a className="era-btn" href="#contact">
                  Build something with me
                </a>
              </div>
            </div>
            <img
              className="era-photo"
              src={photo}
              alt="Eradyan, software engineer"
            />
          </div>
          <div className="era-layers">
            <div>
              <b>1. Software engineering</b>
              Architecture, backend, frontend, APIs, databases, deployment.
            </div>
            <div>
              <b>2. Technical SEO / GEO</b>
              Crawling, rendering, indexing, structured data, AI search.
            </div>
            <div>
              <b>3. Growth &amp; automation</b>
              Analytics, conversion tracking, workflows that scale.
            </div>
          </div>
        </div>
      </header>

      <section className="era-section" id="engineering">
        <div className="era-wrap">
          <span className="era-label">Engineering</span>
          <h2 className="era-h2">Built from the code up.</h2>
          <p className="era-lede">
            Hands-on across the stack: from the schema and the API to the
            interface and the deploy pipeline.
          </p>

          <div className="era-stack">
            {stack.map(([name, items]) => (
              <div className="era-stack-row" key={name}>
                <h3>{name}</h3>
                <p>
                  {items.map(item => (
                    <span key={item}>{item}</span>
                  ))}
                </p>
              </div>
            ))}
          </div>

          <div className="era-think">
            {thinking.map(([label, text]) => (
              <div className="era-think-item" key={label}>
                <span className="era-label">{label}</span>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="era-section" id="work">
        <div className="era-wrap">
          <span className="era-label">Selected work</span>
          <h2 className="era-h2">Code and business, in the same story.</h2>
          <p className="era-lede">
            Each project starts with a business problem, becomes an engineering
            problem, and ends with a system, plus a search and growth layer
            where it applies.
          </p>

          <div className="era-contrast">
            <div>
              <b>Jurnify</b> → Engineering / Automation
            </div>
            <div>
              <b>Biztax</b> → SEO / GEO / Growth
            </div>
          </div>

          <Case
            featured
            tag="Engineering · Automation"
            title="Jurnify"
            sub="Building an accounting automation platform around real financial workflows."
            rows={[
              [
                'Business problem',
                'Accounting teams re-key bank statement lines into their accounting software by hand, which is slow and error-prone.'
              ],
              [
                'Engineering problem',
                'Turn unstructured bank statements into valid journal entries, and push them safely into a third-party accounting system on behalf of authenticated users.'
              ],
              [
                'What I built',
                [
                  'System architecture and backend services',
                  'Authentication and OAuth flow with Accurate Online',
                  'API integration with Accurate Online',
                  'Database design for statements, mappings and journals',
                  'Bank statement processing pipeline',
                  'Journal workflow: review, adjust, post'
                ]
              ],
              [
                'Search / growth layer',
                'Not the point of this project. It is a product, and the engineering is the story.'
              ]
            ]}
            chips={[
              'Node.js',
              'REST APIs',
              'OAuth',
              'PostgreSQL',
              'Accurate Online',
              'Automation'
            ]}
          />

          <Case
            tag="SEO · GEO · Growth"
            title="Biztax"
            sub="Where engineering meets search: content architecture and measurement built as a system."
            rows={[
              [
                'Business problem',
                'Get found by people searching for tax and business-compliance help, and turn that visibility into enquiries.'
              ],
              [
                'Engineering problem',
                'Make the site crawlable, renderable and indexable, with a content structure that both search engines and AI answer engines can parse.'
              ],
              [
                'What I built',
                [
                  'Content architecture and internal linking',
                  'Structured data (schema markup)',
                  'Performance and rendering improvements'
                ]
              ],
              [
                'Search / growth layer',
                [
                  'Technical SEO and GEO / AI search readiness',
                  'Analytics implementation',
                  'Conversion tracking'
                ]
              ]
            ]}
            chips={[
              'Technical SEO',
              'GEO',
              'Structured data',
              'Analytics',
              'Conversion tracking'
            ]}
          />

          <Case
            tag="Engineering + SEO / GEO + Growth"
            title="Saint Labs"
            sub="My own digital engineering brand: the experiment where all three layers meet."
            rows={[
              [
                'Business problem',
                'Prove the whole approach on something I own end to end, without a client brief in the way.'
              ],
              [
                'Engineering problem',
                'Ship a real product and site with fast, crawlable architecture from day one.'
              ],
              [
                'What I built',
                'Application code, deployment, and the site itself as a working lab.'
              ],
              [
                'Search / growth layer',
                'SEO, GEO, analytics and conversion tracking designed in from the start rather than bolted on.'
              ]
            ]}
            chips={['Software', 'SEO', 'GEO', 'Growth', 'Experiments']}
          />
        </div>
      </section>

      <section className="era-section" id="live">
        <div className="era-wrap">
          <span className="era-label">Live projects</span>
          <h2 className="era-h2">Shipped and running in production.</h2>
          <div className="era-live">
            {liveWorks.map(w => (
              <a
                className="era-live-item"
                key={w.name}
                href={w.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <h3>{w.name}</h3>
                <p>{w.org}</p>
                <span>{w.host} ↗</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="era-section" id="search">
        <div className="era-wrap">
          <span className="era-label">The advantage</span>
          <h2 className="era-h2">
            Engineering is what makes my search and growth work different.
          </h2>
          <p className="era-lede">
            I don't hand developers a checklist. I read the codebase, find why a
            page isn't rendering or indexing, and fix it.
          </p>
          <div className="era-adv">
            {advantages.map(a => (
              <div key={a}>{a}</div>
            ))}
          </div>
        </div>
      </section>

      <footer className="era-foot" id="contact">
        <div className="era-wrap">
          <span className="era-label">Contact</span>
          <h2 style={{ marginTop: 14 }}>
            Need software built, or search and growth engineered?
          </h2>
          <div className="era-cta">
            <a className="era-btn primary" href="mailto:eraocsoen@gmail.com">
              eraocsoen@gmail.com
            </a>
          </div>
          <p className="era-copy mt-4">© {new Date().getFullYear()} Eradyan</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
